"use client";

import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { MessageCircle } from "lucide-react";
import { CHAT_TEASER_ACTIVE_EVENT, loadChatwootSdk, openChatwootOrFallback } from "@/lib/chatwoot";
import { useChatwootConfig } from "@/hooks/useChatwootConfig";
import { track } from "@/lib/analytics/mixpanel";

/**
 * Always-visible, fully custom chat launcher — replaces Chatwoot's own bubble
 * (hidden via `hideMessageBubble`, see lib/chatwoot.ts) because a static
 * launcher icon alone was getting almost no clicks. While ChatwootTeaser is
 * showing a message elsewhere on the page, this listens for that and shows a
 * notification dot so the icon visibly connects to the message pointing at it.
 *
 * Rendered globally (root layout) so it's present on every page, mirroring
 * FloatingContact's WhatsApp/Telegram buttons on the opposite corner — but it
 * only paints once chat is actually configured (useChatwootConfig resolves to
 * null on preview deploys with no chat channel).
 */
export default function ChatwootLauncher() {
  const config = useChatwootConfig();
  const [active, setActive] = useState(false);

  useEffect(() => {
    function handleActiveChange(e: Event) {
      setActive(Boolean((e as CustomEvent<boolean>).detail));
    }
    window.addEventListener(CHAT_TEASER_ACTIVE_EVENT, handleActiveChange);
    return () => window.removeEventListener(CHAT_TEASER_ACTIVE_EVENT, handleActiveChange);
  }, []);

  if (!config) return null;

  // Hover/focus/touchstart is a much stronger "about to chat" signal than a
  // blanket preload on every pageview — keeps us under Chatwoot's per-IP
  // new-session rate limit while still feeling instant on click. touchstart
  // covers touch devices, which have no hover before the tap that fires
  // onClick. Idempotent (guarded in loadChatwootSdk), so repeated firings
  // are free.
  function handlePreload() {
    loadChatwootSdk(config!);
  }

  function handleClick() {
    openChatwootOrFallback(config!);
    track("Chat Launcher Clicked", { attention_active: active });
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 lg:bottom-8 lg:right-8">
      <div className="relative flex h-12 w-12 items-center justify-center lg:h-13 lg:w-13">
        <button
          type="button"
          onClick={handleClick}
          onMouseEnter={handlePreload}
          onFocus={handlePreload}
          onTouchStart={handlePreload}
          aria-label="Open live chat"
          title="Chat with us"
          className="relative flex h-12 w-12 items-center justify-center rounded-full bg-brand-purple text-white shadow-lg shadow-brand-purple/30 transition-colors hover:bg-brand-purple-light focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent lg:h-13 lg:w-13"
        >
          <MessageCircle size={22} strokeWidth={1.75} aria-hidden />
        </button>

        {active && (
          <motion.span
            aria-hidden
            className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full bg-status-error ring-2 ring-bg-base"
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.3, 1] }}
            transition={{ duration: 0.4 }}
          />
        )}
      </div>
    </div>
  );
}
